'use client';

import clsx from 'clsx';
import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { useSanityImage } from '@/components/SanityImage';
import {
  BASE_FADEIN_DURATION,
  BASE_Y,
  DEFAULT_DELAY,
  getFadeInAndUpProps,
} from 'app/(user)/_utils/shared-animations';
import styles from './bento-grid.module.css';
import { Text } from '@/components/Text/Text';
import { IconArrowRight } from '@frameio/vapor-icons';
import Image from 'next/image';
import Link from 'next/link';
import { theme } from '@frameio/vapor-theme';
import type { BentoGrid } from './types';
import type { BentoCard, BentoImageCard } from './BentoCard/types';
import { VideoPlayer } from '@/blocks/VideoPlayer/VideoPlayer';

function ImageCard(content: BentoImageCard) {
  const { image } = content;
  const { height, imageSource, loader, lqip, width } = useSanityImage({
    src: image,
  });
  return (
    <Link href={content.url} className={clsx(styles.item, styles.imageCard)}>
      {image && (
        <>
          <div className={styles.imageContainer}>
            <Image
              alt=""
              blurDataURL={lqip}
              height={height}
              loader={loader}
              placeholder={lqip ? 'blur' : 'empty'}
              src={imageSource}
              style={{
                borderRadius: '24px',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                overflow: 'hidden',
              }}
              width={width}
            />
          </div>
          <div className={clsx(styles.imageGradient)} />
        </>
      )}
      <div className={styles.textBlock}>
        <Text as="h3" color="primary" variant="title3" className={styles.title}>
          {content.title}
        </Text>
        {content.cta && (
          <div className={styles.ctaContainer}>
            <Text as="p" color="tertiary" variant="link">
              {content.cta}
            </Text>
            <IconArrowRight color={theme.colors.icon.primary} size={16} />
          </div>
        )}
      </div>
    </Link>
  );
}

function VideoCard(content: BentoCard) {
  const { video } = content;
  return (
    <div className={clsx(styles.item, styles.videoCard)}>
      {video && (
        <>
          <VideoPlayer
            key={video._key}
            url={video.asset?.videoFile}
            thumbnail={video.asset?.thumbnail}
            thumbnailProps={{ objectFit: 'cover', style: { height: '100%' } }}
            alt={video.alt}
            style={{ borderRadius: '24px', overflow: 'hidden' }}
          />
        </>
      )}
      <div className={styles.textBlock}>
        <Text as="h3" color="primary" variant="title3" className={styles.title}>
          {content.title}
        </Text>
      </div>
    </div>
  );
}

function TextCard(content: BentoCard) {
  return (
    <Link href={content.url} className={clsx(styles.item, styles.textCard)}>
      <div className={styles.textBlock}>
        <div className={styles.headerPill}>
          <Text color="tertiary" variant="link" className={styles.title}>
            {content.title}
          </Text>
        </div>
        <p className={styles.displayText}>{content.displayText}</p>
      </div>
    </Link>
  );
}

export function BentoGrid({
  cards,
  style,
}: Pick<BentoGrid, 'cards'> & { style?: CSSProperties }) {
  const startingY = BASE_Y * -4;
  return (
    <motion.div
      className={styles.grid}
      style={style}
      {...getFadeInAndUpProps({
        startingY,
        duration: BASE_FADEIN_DURATION * 3,
        delay: DEFAULT_DELAY * 3,
      })}
    >
      {cards.map((card) => {
        switch (card.visual) {
          case 'image':
            return <ImageCard {...card} key={card.title} />;
          case 'video':
            return <VideoCard {...card} key={card.title} />;
          case 'text':
            return <TextCard {...card} key={card.title} />;
        }
      })}
    </motion.div>
  );
}
